import { mergeRight, hasPath, assocPath, dissocPath, assoc, pathOr, prop, path, propOr } from 'ramda';
import moment from 'moment';
import { CanonicalLoader } from 'vms-state';
import def from './define-action';
import * as Gtag from '../gtag';
import readyAnimation from '../../ext/publish-animations/ready.json';
import publishAnimation from '../../ext/publish-animations/publish.json';
import successAnimation from '../../ext/publish-animations/success.json';
import errorAnimation from '../../ext/publish-animations/error.json';

const INITIAL_STATE = {
    status: {},
    errorMessage: '',
    banner: { icon: '', message: '', isVisible: false },
    animationOptions: {},
    lastSuccessDate: new Date(),
    publishing: {},
    interval: {}, // interval for updating status for the publish page
};

const PUBLISH_STATUS = {
    PUBLISHING: 'PUBLISHING',
    ERROR: 'ERROR',
};

const STATUS = {
    FAILED: 'FAILED',
    RUNNING: 'RUNNING',
    SUCCEEDED: 'SUCCEEDED',
};

const BANNER_ICON = {
    FAILED: 'icon-error',
    RUNNING: 'icon-fluidloader',
    SUCCEEDED: 'icon-circle-check',
};

const BANNER_MESSAGE = {
    FAILED: 'There was an error publishing your changes. The Atrius map team has been notified.',
    RUNNING: 'Publish in progress',
    SUCCEEDED: 'Publish successfully deployed to production at',
};

const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: readyAnimation,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

const getBannerColor = status => {
    switch (status) {
        case 'RUNNING':
            return 'blue';
        case 'SUCCEEDED':
            return 'green';
        default:
            return 'red';
    }
};
// stopDate arg used here is actually lastSuccessDate in state
const getBannerOptions = (status, startDate, stopDate) => {
    let isVisible = !(status === STATUS.SUCCEEDED && moment().diff(moment(stopDate), 'hours') > 12);

    let icon = BANNER_ICON[status] || 'icon-fluidloader';
    let color = getBannerColor(status);
    let message = BANNER_MESSAGE[status] || 'Still loading...';

    // edit the message to use the startDate / stopDate
    if (status === STATUS.RUNNING) message = `${message} (started ${moment(startDate).fromNow()})`;

    if (status === STATUS.SUCCEEDED) message = `${message} ${moment(stopDate).format('LT')} (completed in ${moment(stopDate).diff(moment(startDate), 'minutes')} minutes)`;

    if (stopDate === "UNKNOWN") {
        isVisible = true
        icon = 'icon-error'
        message = 'Unable to find last successful publish. This venue should be published to make sure it is accurate.'
        color = 'red'

    }
    return { icon, message, isVisible, color };
};

const handleReady = () => mergeRight(defaultOptions, { animationData: readyAnimation });
const handlePublish = () => mergeRight(defaultOptions, { animationData: publishAnimation });
const handleSuccess = () => mergeRight(defaultOptions, { animationData: successAnimation });
const handleError = () => mergeRight(defaultOptions, { animationData: errorAnimation });

const handleReadyOrSuccess = stopDate => {
    const showSuccess = moment().diff(moment(stopDate), 'hours') <= 12;

    if (showSuccess) return handleSuccess();

    return handleReady();
};

const getAnimationOptions = (status, stopDate) => {
    switch (status) {
        case STATUS.FAILED:
            return handleError();
        case STATUS.RUNNING:
            return handlePublish();
        case STATUS.SUCCEEDED:
            return handleReadyOrSuccess(stopDate);
        default:
            return defaultOptions;
    }
};

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    BEGIN_GET_STATUS: 'BEGIN_GET_STATUS',
    BEGIN_PUBLISH: 'BEGIN_PUBLISH',
    CLEAR_STATUS_INTERVAL: 'CLEAR_STATUS_INTERVAL',
    PUBLISH_FAILURE: 'PUBLISH_FAILURE',
    PUBLISH_SUCCESS: 'PUBLISH_SUCCESS',
    SET_ANIMATION_OPTIONS: 'SET_ANIMATION_OPTIONS',
    SET_ASSET_STATUS: 'SET_ASSET_STATUS',
    SET_LAST_SUCCESS: 'SET_LAST_SUCCESS',
    SET_STATUS_BANNER: 'SET_STATUS_BANNER',
    START_STATUS_INTERVAL: 'START_STATUS_INTERVAL',
    STATUS_FAILURE: 'STATUS_FAILURE',
    STATUS_SUCCESS: 'STATUS_SUCCESS',
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    beginGetStatus: def(ActionConstants.BEGIN_GET_STATUS, 'venueId'),
    beginPublish: def(ActionConstants.BEGIN_PUBLISH, 'venueId'),
    clearStatusInterval: def(ActionConstants.CLEAR_STATUS_INTERVAL, 'venueId'),
    publishFailure: def(ActionConstants.PUBLISH_FAILURE, 'venueId', 'errorMessage'),
    publishSuccess: def(ActionConstants.PUBLISH_SUCCESS, 'venueId'),
    setAnimationOptions: def(ActionConstants.SET_ANIMATION_OPTIONS, 'venueId'),
    setAssetStatus: def(ActionConstants.SET_ASSET_STATUS, 'venueId', 'status'),
    setLastSuccessDate: def(ActionConstants.SET_LAST_SUCCESS, 'stopDate'),
    setStatusBanner: def(ActionConstants.SET_STATUS_BANNER, 'venueId'),
    startStatusInterval: def(ActionConstants.START_STATUS_INTERVAL, 'venueId', 'interval'),
    statusFailure: def(ActionConstants.STATUS_FAILURE, 'venueId', 'errorMessage'),
    statusSuccess: def(ActionConstants.STATUS_SUCCESS, 'venueId'),
};
// =====================================================================================================================
// Thunks
// =====================================================================================================================

/*
 * Get the last successful publish to have the last publish date
 * @sig getLastPublishSuccess :: VenueId -> Dispatch -> Object
 */
// This only gets called when a new venue selected, not the interval
const getLastPublishSuccess = venueId => async dispatch => {
    dispatch(Actions.beginGetStatus(venueId));
    
    try {
        const status = await CanonicalLoader.getPublishStatus(venueId, STATUS.SUCCEEDED);
        dispatch(Actions.setLastSuccessDate(status ? status.stopDate : "UNKNOWN"));
        dispatch(Actions.statusSuccess(venueId));
    } catch (e) {
        console.error(`Error in getLastPublishSuccess`, e);
        dispatch(Actions.statusFailure(venueId, e));
        dispatch(Actions.clearStatusInterval(venueId));
    }
};

/*
 * Get the status of the latest step function
 * @sig getPublishAssetStatus :: VenueId -> Dispatch -> Object
 */
// This gets called whenever a new venue is selected, and then every 5 seconds after
const getPublishAssetStatus = venueId => async (dispatch, getState) => {
    dispatch(Actions.beginGetStatus(venueId));
    try {
        const status = await CanonicalLoader.getPublishStatus(venueId, '', getState().interval);

        dispatch(Actions.setAssetStatus(venueId, status)); //for a particular venue sets 'executionStatus', 'startDate', 'stopDate'
        dispatch(Actions.setStatusBanner(venueId));
        dispatch(Actions.setAnimationOptions(venueId));

        // Update the last successful publish if the times are different
        if (status.executionStatus === STATUS.SUCCEEDED && moment(status.stopDate).isAfter(getLastSuccessDate(getState()))) {
            dispatch(Actions.setLastSuccessDate(status.stopDate));
        }

        dispatch(Actions.statusSuccess(venueId)); // this sets the state "status" for the venue
    } catch (e) {
        console.error(e);
        dispatch(Actions.statusFailure(venueId, e));
        dispatch(Actions.clearStatusInterval(venueId));
    }
};

/*
 * Publish this venue
 * @sig publishVenue :: VenueId -> Dispatch -> ()
 */
const publishVenue = venueId => async dispatch => {
    dispatch(Actions.beginPublish(venueId));
    try {
        Gtag.recordPublish(venueId);
        await CanonicalLoader.publish(venueId);
        await dispatch(getPublishAssetStatus(venueId));

        dispatch(Actions.publishSuccess(venueId));
        return true;
    } catch (e) {
        console.error(e);
        dispatch(Actions.publishFailure(venueId, e));
        return false;
    }
};

const beginStatusInterval = venueId => async (dispatch, getState) => {
    try {
        const state = getState();
        if (hasPath(['publishStatus', 'interval', venueId], state)) return;

        const interval = setInterval(() => {
            dispatch(getPublishAssetStatus(venueId));
        }, 5000);

        dispatch(Actions.startStatusInterval(venueId, interval));
    } catch (e) {
        console.error(e);
    }
};

const Thunks = {
    beginStatusInterval,
    getLastPublishSuccess,
    getPublishAssetStatus,
    publishVenue,
};

// =====================================================================================================================
// Reducer
// =====================================================================================================================

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case ActionConstants.BEGIN_GET_STATUS:
            return handleBeginGetStatus(state, action);
        case ActionConstants.BEGIN_PUBLISH:
            return handleBeginPublish(state, action);
        case ActionConstants.PUBLISH_FAILURE:
            return handlePublishFailure(state, action);
        case ActionConstants.PUBLISH_SUCCESS:
            return handlePublishSuccess(state, action);
        case ActionConstants.SET_ASSET_STATUS:
            return handleSetStatus(state, action);
        case ActionConstants.STATUS_FAILURE:
            return handleStatusFailure(state, action);
        case ActionConstants.STATUS_SUCCESS:
            return handleStatusSuccess(state, action);

        case ActionConstants.SET_STATUS_BANNER:
            return handleSetStatusBanner(state, action);
        case ActionConstants.SET_ANIMATION_OPTIONS:
            return handleSetAnimationOptions(state, action);
        case ActionConstants.SET_LAST_SUCCESS:
            return handleSetLastSuccess(state, action);
        case ActionConstants.START_STATUS_INTERVAL:
            return handleStartStatusInterval(state, action);
        case ActionConstants.CLEAR_STATUS_INTERVAL:
            return handleClearStatusInterval(state, action);

        default:
            return state;
    }
};

const handleBeginGetStatus = (state, action) => state;

const handleStatusFailure = (state, action) => state;

const handleStatusSuccess = (state, action) => state;

const handleBeginPublish = (state, action) => assocPath(['publishing', action.venueId], PUBLISH_STATUS.PUBLISHING, state);
const handlePublishFailure = (state, action) => assocPath(['publishing', action.venueId], PUBLISH_STATUS.ERROR, state);
const handlePublishSuccess = (state, action) => dissocPath(['publishing', action.venueId], state);
const handleSetStatus = (state, action) => assocPath(['status', action.venueId], action.status, state);

const handleSetStatusBanner = (state, action) => {
    const { executionStatus, startDate } = pathOr({}, ['status', action.venueId], state);

    const stopDate = prop(['lastSuccessDate'], state);
    const banner = getBannerOptions(executionStatus, startDate, stopDate);

    return assoc('banner', banner, state);
};

const handleSetAnimationOptions = (state, action) => {
    const { executionStatus } = path(['status', action.venueId], state);
    const stopDate = prop(['lastSuccessDate'], state);
    const animationOptions = getAnimationOptions(executionStatus, stopDate);

    return assoc('animationOptions', animationOptions, state);
};

const handleSetLastSuccess = (state, action) => assoc('lastSuccessDate', action.stopDate, state);

const handleStartStatusInterval = (state, action) => assocPath(['interval', action.venueId], action.interval, state);

const handleClearStatusInterval = (state, action) => {
    const interval = path(['interval', action.venueId], state);

    if (interval) {
        clearInterval(interval);
        return dissocPath(['interval', action.venueId], state);
    }

    return state;
};

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const getStatus = (venueId, masterState) => {
    const status = path(['publishStatus', 'status'], masterState)[venueId];
    return status || { executionStatus: '', startDate: new Date(), stopDate: new Date() };
};

const getAssetsError = masterState => path(['publishStatus', 'errorMessage'], masterState);
const getStatusBanner = masterState => path(['publishStatus', 'banner'], masterState);
const getStatusAnimation = masterState => path(['publishStatus', 'animationOptions'], masterState);
const getLastSuccessDate = masterState => path(['publishStatus', 'lastSuccessDate'], masterState);

const isPublishPending = (venueId, masterState) => {
    const result = pathOr('', ['publishStatus', 'publishing', venueId], masterState);
    const status = propOr('', 'executionStatus', getStatus(venueId, masterState));

    // If we're calling publish or the status is 'RUNNING' or 'FAILED' publish should be pending and the button disabled
    return result === PUBLISH_STATUS.PUBLISHING || status === 'RUNNING' || status === 'FAILED';
};

const Selectors = {
    getAssetsError,
    getLastSuccessDate,
    isPublishPending,
    getStatus,
    getStatusAnimation,
    getStatusBanner,
};

export default reducer;

export { reducer, Actions, Selectors, Thunks, handleSetStatusBanner };
